import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Linkedin, Twitter, Facebook, Instagram, Youtube, MapPin, MessageCircleQuestion } from 'lucide-react';
import { usePlatformSettings, resolveMediaUrl } from '@/lib/platformSettings';
import FeedbackDialog from '@/components/marketplace/FeedbackDialog';

const SOCIAL_ICONS = {
  linkedin: Linkedin,
  twitter: Twitter,
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
};

// Renders an internal Link for relative URLs and a plain <a> for absolute ones
const FooterLink = ({ href, children, testid }) => {
  const isExternal = /^(https?:)?\/\//i.test(href || '') || /^mailto:|^tel:/i.test(href || '');
  if (isExternal) {
    return (
      <a
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className='hover:text-foreground'
        data-testid={testid}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={href || '/'} className='hover:text-foreground' data-testid={testid}>
      {children}
    </Link>
  );
};

export const Footer = () => {
  const { brand, footer } = usePlatformSettings();

  const brandName = brand?.brand_short_name || 'IndusFact';
  const brandLong = brand?.brand_name || 'IndusFact B2B Solutions';
  const brandSuffix = brandLong.replace(brandName, '').trim() || 'B2B Classifieds';
  const logoSrc = brand?.logo_url ? resolveMediaUrl(brand.logo_url) : '/indusfact-logo.png';
  const supportEmail = brand?.support_email || 'support@indusfact.com';
  const supportPhone = brand?.support_phone || '';
  const address = brand?.address || '';

  const aboutText = footer?.about_text
    || "India's industrial classified platform to buy, sell, and rent machines, tools, spares, raw materials, and equipment. Verified listings. Direct inquiries. No middlemen.";
  const columns = Array.isArray(footer?.columns) && footer.columns.length ? footer.columns : [];
  const showSocial = footer?.show_social !== false;
  const social = brand?.social || {};
  const activeSocials = Object.entries(social).filter(([, url]) => !!url);

  const year = new Date().getFullYear();
  const copyright = (footer?.copyright || `© {year} ${brandLong}. All rights reserved.`).replace('{year}', String(year));
  const bottomTagline = footer?.bottom_tagline || 'Made for Indian industry · Verified · Moderated';

  // Adapt grid columns based on number of admin-defined columns (1..4 renders neat, more wrap)
  const gridColClass = columns.length >= 3
    ? 'grid-cols-2 md:grid-cols-4'
    : columns.length === 2
      ? 'grid-cols-2 md:grid-cols-3'
      : 'grid-cols-2 md:grid-cols-2';

  return (
    <footer className='border-t bg-secondary/40 mt-16' data-testid='site-footer'>
      <div className={`indus-container py-12 grid ${gridColClass} gap-8 text-sm`}>
        {/* Brand block */}
        <div className='col-span-2 md:col-span-1'>
          <div className='flex items-center gap-2'>
            <span className='inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white border overflow-hidden'>
              <img
                src={logoSrc}
                alt={brandName}
                className='h-8 w-8 object-contain'
                data-testid='footer-logo-img'
              />
            </span>
            <div>
              <div className='font-semibold' data-testid='footer-brand-name'>{brandName}</div>
              <div className='text-xs text-muted-foreground'>{brandSuffix}</div>
            </div>
          </div>
          <p className='mt-4 text-muted-foreground' data-testid='footer-about-text'>
            {aboutText}
          </p>
          <div className='mt-4 space-y-2 text-muted-foreground'>
            <div className='flex items-center gap-2'>
              <Mail className='h-4 w-4' />
              <a href={`mailto:${supportEmail}`} className='hover:text-foreground' data-testid='footer-email'>{supportEmail}</a>
            </div>
            {supportPhone && (
              <div className='flex items-center gap-2' data-testid='footer-phone'>
                <Phone className='h-4 w-4' />
                <a href={`tel:${supportPhone.replace(/\s/g, '')}`} className='hover:text-foreground'>{supportPhone}</a>
              </div>
            )}
            {address && (
              <div className='flex items-start gap-2' data-testid='footer-address'>
                <MapPin className='h-4 w-4 mt-0.5 shrink-0' />
                <span className='whitespace-pre-line'>{address}</span>
              </div>
            )}
          </div>

          {showSocial && activeSocials.length > 0 && (
            <div className='mt-4 flex items-center gap-2' data-testid='footer-socials'>
              {activeSocials.map(([net, url]) => {
                const Icon = SOCIAL_ICONS[net];
                if (!Icon) return null;
                return (
                  <a
                    key={net}
                    href={url}
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-label={net}
                    className='h-8 w-8 inline-flex items-center justify-center rounded-full border bg-background text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors'
                    data-testid={`footer-social-${net}`}
                  >
                    <Icon className='h-4 w-4' />
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {/* Editable columns */}
        {columns.map((col) => (
          <div key={col.id || col.heading} data-testid={`footer-column-${col.id || col.heading}`}>
            <div className='font-semibold mb-3'>{col.heading}</div>
            <ul className='space-y-2 text-muted-foreground'>
              {(col.links || []).map((ln, idx) => (
                <li key={`${col.id || col.heading}-${idx}`}>
                  <FooterLink href={ln.url} testid={`footer-link-${(col.id || col.heading).toLowerCase()}-${idx}`}>
                    {ln.label}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Fallback columns when admin has left footer.columns empty */}
        {columns.length === 0 && (
          <>
            <div>
              <div className='font-semibold mb-3'>Marketplace</div>
              <ul className='space-y-2 text-muted-foreground'>
                <li><Link to='/sell' className='hover:text-foreground'>Sell Listings</Link></li>
                <li><Link to='/wanted' className='hover:text-foreground'>Wanted / Buy Requirements</Link></li>
                <li><Link to='/rent' className='hover:text-foreground'>Rent Equipment</Link></li>
                <li><Link to='/categories' className='hover:text-foreground'>All Categories</Link></li>
                <li><Link to='/locations' className='hover:text-foreground'>Browse by Location</Link></li>
              </ul>
            </div>
            <div>
              <div className='font-semibold mb-3'>Company</div>
              <ul className='space-y-2 text-muted-foreground'>
                <li><Link to='/about' className='hover:text-foreground'>About Us</Link></li>
                <li><Link to='/contact' className='hover:text-foreground'>Contact</Link></li>
                <li><Link to='/blog' className='hover:text-foreground'>Blog</Link></li>
              </ul>
            </div>
            <div>
              <div className='font-semibold mb-3'>Policies</div>
              <ul className='space-y-2 text-muted-foreground'>
                <li><Link to='/privacy' className='hover:text-foreground'>Privacy Policy</Link></li>
                <li><Link to='/terms' className='hover:text-foreground'>Terms &amp; Conditions</Link></li>
              </ul>
            </div>
          </>
        )}
      </div>

      <div className='border-t'>
        <div className='indus-container py-4 text-xs text-muted-foreground flex flex-col sm:flex-row justify-between gap-2'>
          <span data-testid='footer-copyright'>{copyright}</span>
          <div className='flex items-center gap-4'>
            <FeedbackDialog
              source='footer'
              trigger={
                <button
                  type='button'
                  className='inline-flex items-center gap-1 hover:text-foreground underline'
                  data-testid='footer-feedback-link'
                >
                  <MessageCircleQuestion className='h-3.5 w-3.5' /> Send feedback
                </button>
              }
            />
            <span data-testid='footer-bottom-tagline'>{bottomTagline}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
